import { TFunction, Trans, useTranslation } from "react-i18next";
import { Card, Link, Text } from "theme-ui";
import type { JSX } from "react";
import * as API from "lib/api/types";
import routes from "routes";

const MemberStatusMessage = ({
  userProfile,
  t,
}: {
  userProfile: API.UserProfile;
  t: TFunction<"translation", undefined>;
}): JSX.Element => {
  /*
  The <Trans /> component replaces used below and interpolates its over its text children.
  Text inside is for placeholder purposes only.
  example:
    <Trans i18nKey="foo.bar">
      click <a href="something">here</a>!
    </Trans>
  Trans children: ['click', <a>here</a>, '!'] -> [text, element with text children, text]
  Its english translation would look like this: "Click <1>here</1>!"
  Reference: https://react.i18next.com/latest/trans-component
  */
  switch (userProfile.memberStatus) {
    case API.MemberStatus.MEMBER:
      return t("messages.memberDetails.member");
    case API.MemberStatus.LAPSED_MEMBER:
      return (
        <Trans i18nKey="messages.memberDetails.lapsedMember">
          placeholder for messages.memberDetails.lapsedMember: Your dues are lapsed. See
          the
          <Link target="_blank" rel="noopener noreferrer" href={routes.external.renew}>
            FAQ Link
          </Link>
          .
        </Trans>
      );
    case API.MemberStatus.FORMER_MEMBER:
      return (
        <Trans i18nKey="messages.memberDetails.formerMember">
          placeholder for messages.memberDetails.formerMember: You are a former member.
          See the
          <Link target="_blank" rel="noopener noreferrer" href={routes.external.renew}>
            FAQ Link
          </Link>
          .
        </Trans>
      );
    default:
      return t("messages.memberDetails.nonmemberFallback");
  }
};

const MemberDetails = ({
  userProfile,
}: {
  userProfile: API.UserProfile;
}): JSX.Element => {
  const {
    t,
    i18n: { language },
  } = useTranslation();

  const joinDate = userProfile.joinDate && new Date(userProfile.joinDate);
  const joinDateFormatted = joinDate?.toLocaleString(language, {
    month: "long",
    year: "numeric",
  });
  return (
    <Card
      sx={{
        display: "flex",
        flexDirection: "column",
        p: 2,
        pl: 4,
        borderStyle: "solid",
        borderWidth: 2,
        borderColor: "darkRed",
        backgroundColor: "notifications.info",
      }}
    >
      {joinDateFormatted && (
        <Text sx={{ lineHeight: 2 }}>
          {`${t("messages.memberDetails.joinDate")}: ${joinDateFormatted}`}
        </Text>
      )}
      <Text>
        <MemberStatusMessage userProfile={userProfile} t={t} />
      </Text>
    </Card>
  );
};

export default MemberDetails;
